import React from 'react';
import ReactDOM from 'react-dom';
import { I18nextProvider } from 'react-i18next';
import DevIntegration from '@/dev/components/DevIntegration';
import PaymentPage from '@/payment/PaymentPage';
import i18n from '@/lib/config/i18n';
import NotFound from '@/lib/components/NotFound';
import * as Sentry from '@sentry/react';

import './main.scss';
import PaymentPlanPage from './payment/PaymentPlanPage';
import ProcessingPage from './payment/ProcessingPage';
import {GlobalContextProvider} from '@/lib/context/global/index.js';
import { NotificationHost } from '@/lib/components/Notification';

// Parse and inject weezConfig if not present
if (!window.weezConfig && import.meta.env.VITE_WEEZ_CONFIG) {
    try {
        window.weezConfig = JSON.parse(import.meta.env.VITE_WEEZ_CONFIG);
    } catch (e) {
        console.error('Failed to parse VITE_WEEZ_CONFIG', e);
    }
}

const shouldDisplayPaymentPlanPage = window.location.pathname.includes('pay-over-time');
const isProcessingPage = window.location.pathname.endsWith('/processing');
const shouldShowPaymentPage = window.location.pathname !== '/' && !shouldDisplayPaymentPlanPage && !isProcessingPage;
const shouldShowDevIntegration = !shouldShowPaymentPage && !shouldDisplayPaymentPlanPage && !isProcessingPage && window.weezConfig?.environment !== 'production';
const shouldShowNotFound = !shouldShowPaymentPage && !shouldShowDevIntegration && !shouldDisplayPaymentPlanPage && !isProcessingPage;

if (window.weezConfig?.sentry) {
    Sentry.init({
        dsn: window.weezConfig.sentry.dsn,
        environment: window.weezConfig.sentry.environment,
        release: (import.meta.env.REACT_APP_RELEASE || 'NO_RELEASE') + ((import.meta.env.PAYMENT_PAGE ?? false) ? '-PP' : '-lib'),
        integrations: [
            Sentry.browserTracingIntegration(),
        ],
    });
}
/**
 * This is the entry point.
 * Otherwise, LyriaPay is used as a library and so not as a running application.
 */
/** 
 * As of December 2025, a hidden iframe (iframe3dsmethod) is trying to load our page inside it,
 * possibly because it's trying to load callback_url, so "if (window.self === window.top)" is added here as a "hack".
 * It is used to prevent any iframe from rendering our entire payment page inside it,
 * since it can trigger unintended backend calls and mess up our payment workflow.
 * NOTE: due to this, if at some point we decide to put our payment page in an iframe somewhere -> this will return a blank page
*/
if (window.self === window.top) {
    ReactDOM.render(
        <React.StrictMode>
            <I18nextProvider i18n={i18n}>
                <GlobalContextProvider>
                    <NotificationHost />
                    {shouldDisplayPaymentPlanPage && <PaymentPlanPage />}
                    {isProcessingPage && <ProcessingPage />}
                    {shouldShowPaymentPage && <PaymentPage />}
                    {shouldShowDevIntegration && <DevIntegration />}
                    {shouldShowNotFound && <NotFound />}
                </GlobalContextProvider>
            </I18nextProvider>
        </React.StrictMode>,
        document.getElementById('root'),
    );
}
